import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CampaignIcon from '@mui/icons-material/Campaign';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import apiService from '../services/api';
import type { AnnouncementPopup } from '../types';

/**
 * 공지 팝업 목록.
 * 앱 시작 시 노출되는 공지 팝업을 CRUD.
 */
export default function AnnouncementList() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AnnouncementPopup[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getAnnouncements({
        page: page + 1,
        pageSize,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? '목록 조회 실패');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (id: number, current: boolean) => {
    try {
      await apiService.updateAnnouncement(id, { isActive: !current });
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '상태 변경 실패');
    }
  };

  const handleDelete = async (id: number, title: string) => {
    if (!window.confirm(`"${title}" 공지를 삭제하시겠습니까?`)) return;
    try {
      await apiService.deleteAnnouncement(id);
      load();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? '삭제 실패');
    }
  };

  // 현재 노출 여부 (기간 계산)
  const isLiveNow = (a: AnnouncementPopup): boolean => {
    if (!a.isActive) return false;
    const now = Date.now();
    return (
      new Date(a.showFrom).getTime() <= now &&
      new Date(a.showUntil).getTime() >= now
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <CampaignIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">
            공지 팝업 관리
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/announcements/new')}
        >
          새 공지 등록
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>제목</TableCell>
              <TableCell>노출 기간</TableCell>
              <TableCell>우선순위</TableCell>
              <TableCell>이미지</TableCell>
              <TableCell>노출 중</TableCell>
              <TableCell>활성</TableCell>
              <TableCell align="right">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    등록된 공지가 없습니다
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {a.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {a.content}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {new Date(a.showFrom).toLocaleString('ko-KR')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ~ {new Date(a.showUntil).toLocaleString('ko-KR')}
                    </Typography>
                  </TableCell>
                  <TableCell>{a.priority}</TableCell>
                  <TableCell>
                    {a.imageUrl ? (
                      <img
                        src={a.imageUrl}
                        alt=""
                        style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        없음
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {isLiveNow(a) ? (
                      <Chip size="small" label="노출 중" color="success" />
                    ) : (
                      <Chip size="small" label="대기/종료" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={a.isActive}
                      onChange={() => handleToggleActive(a.id, a.isActive)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="편집">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/announcements/edit/${a.id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="삭제">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(a.id, a.title)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="페이지당"
        />
      </TableContainer>
    </Box>
  );
}
